<template>
  <div :class="labelClass" :style="labelStyle" :title="description">
    <span v-if="icon" class="label-action--button">
      <fluent-icon :icon="icon" size="12" class="label--icon cursor-pointer" />
    </span>
    <span
      v-if="['smooth', 'dashed'].includes(variant) && title && !icon"
      :style="{ background: color }"
      class="label-color-dot"
    />
    <span v-if="!href">{{ title }}</span>
    <a v-else :href="href" :style="anchorStyle">{{ title }}</a>
    <!-- Icon-only, so it carries the only name a screen reader will hear. -->
    <button
      v-if="showClose"
      type="button"
      class="label-close--button"
      :aria-label="`${$t('GENERAL_SETTINGS.DISMISS')} ${title}`"
      :style="{ color: textColor }"
      @click="onClick"
    >
      <fluent-icon icon="dismiss" size="12" class="close--icon" />
    </button>
  </div>
</template>
<script>
import { contrastingTextColor } from 'shared/helpers/contrastHelper';

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    bgColor: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
  },
  computed: {
    textColor() {
      if (this.variant === 'smooth') return '';
      if (this.variant === 'dashed') return '';
      return this.color || contrastingTextColor(this.bgColor);
    },
    labelClass() {
      return `label ${this.colorScheme} ${this.variant} ${
        this.small ? 'small' : ''
      }`;
    },
    labelStyle() {
      if (this.bgColor) {
        return {
          background: this.bgColor,
          color: this.textColor,
          border: `1px solid ${this.bgColor}`,
        };
      }
      return {};
    },
    anchorStyle() {
      if (this.bgColor) {
        return { color: this.textColor };
      }
      return {};
    },
  },
  methods: {
    onClick() {
      this.$emit('click', this.title);
    },
  },
};
</script>

<style scoped lang="scss">
.label {
  // `me-1` rather than an `mr`/`rtl:ml` pair: one declaration that is already
  // correct in both directions.
  @apply inline-flex items-center font-medium gap-1.5 me-1 mb-1 px-2.5 py-1 rounded-full bg-slate-75/70 dark:bg-white/[0.07] text-slate-800 dark:text-slate-100 border border-solid border-slate-100 dark:border-white/[0.08] h-6;

  &.small {
    @apply text-xs py-0.5 px-1.5 leading-tight h-5 rounded-full;
  }

  &.small .label--icon,
  &.small .close--icon {
    @apply text-[0.5rem];
  }

  a {
    @apply text-xs rounded-full;
    &:hover {
      @apply underline;
    }

    &:focus-visible {
      @apply outline-none ring-2 ring-accent-mint/70 ring-offset-2 ring-offset-white dark:ring-offset-ink;
    }

    &:focus:not(:focus-visible) {
      @apply outline-none;
    }
  }

  /* Color Schemes */
  &.primary {
    @apply bg-woot-600/[0.12] dark:bg-woot-500/[0.20] text-woot-900 dark:text-woot-50 border-0;

    a {
      @apply text-woot-900 dark:text-woot-900;
    }
    .label-color-dot {
      @apply bg-woot-600 dark:bg-woot-600;
    }
  }
  &.secondary {
    @apply bg-slate-75/70 dark:bg-white/[0.07] text-slate-900 dark:text-slate-100 border border-solid border-slate-100 dark:border-white/[0.08];

    a {
      @apply text-slate-900 dark:text-slate-100;
    }
    .label-color-dot {
      @apply bg-slate-600 dark:bg-slate-600;
    }
  }
  &.success {
    @apply bg-green-600/[0.12] dark:bg-green-500/[0.20] text-green-900 dark:text-green-100 border-0;

    a {
      @apply text-green-900 dark:text-green-100;
    }
    .label-color-dot {
      @apply bg-green-600 dark:bg-green-600;
    }
  }
  &.alert {
    @apply bg-red-600/[0.12] dark:bg-red-500/[0.20] text-red-900 dark:text-red-100 border-0;

    a {
      @apply text-red-900 dark:text-red-100;
    }
    .label-color-dot {
      @apply bg-red-600 dark:bg-red-600;
    }
  }
  &.warning {
    @apply bg-yellow-600/[0.12] dark:bg-yellow-500/[0.20] text-yellow-900 dark:text-yellow-100 border-0;

    a {
      @apply text-yellow-900 dark:text-yellow-100;
    }
    .label-color-dot {
      @apply bg-yellow-900 dark:bg-yellow-900;
    }
  }

  &.smooth {
    @apply bg-transparent text-slate-700 dark:text-slate-100 border border-solid border-slate-100 dark:border-slate-700;
  }

  &.dashed {
    @apply bg-transparent text-slate-700 dark:text-slate-100 border border-dashed border-slate-100 dark:border-slate-700;
  }
}

.label-close--button {
  @apply text-slate-800 dark:text-slate-100 -mb-0.5 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-150 ease-out hover:bg-slate-100 dark:hover:bg-slate-700;

  &:focus-visible {
    @apply outline-none ring-2 ring-accent-mint/70 ring-offset-2 ring-offset-white dark:ring-offset-ink;
  }

  &:focus:not(:focus-visible) {
    @apply outline-none;
  }

  @media (prefers-reduced-motion: reduce) {
    @apply transition-none;
  }
}

.label-action--button {
  @apply flex me-1;
}

// A round dot, separated by its own edge rather than a drop shadow — hairlines are
// the separation language here, not elevation.
.label-color-dot {
  @apply inline-block w-3 h-3 rounded-full;
}
.label.small .label-color-dot {
  @apply w-2 h-2 rounded-full;
}
</style>
